import React, { useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import {
  IconArrowUp,
  IconArrowDown,
  IconCopy,
  IconTrash,
} from '@tabler/icons-react';
import type {
  AdditionalField,
  AdditionalFieldType,
  FieldStrictness,
  LicensePickerConfig,
} from '../../../types/ticketTypeTemplate';
import { FIELD_DEFAULT_RESOLVER_REGISTRY } from '../../../utils/templateEngine';
import { ConditionEditor } from '../../shared/ConditionEditor';
import { OptionsEditor } from './OptionsEditor';
import { LicensePickerConfigEditor } from './LicensePickerConfigEditor';
import { EditorCard } from '../EditorCard';
import { Chip } from '../../chip';
import { FIELD_SX } from '../../../styles/designSystem';

const CANCEL_BTN_SX = { color: '#888', '&:hover': { color: '#555', bgcolor: 'transparent' } };

interface AdditionalFieldEditorProps {
  field: AdditionalField;
  /** All field keys in the parent template (including this one) — used to seed Autocomplete in the condition editor. */
  siblingFieldKeys: string[];
  onChange: (updated: AdditionalField) => void;
  onDelete: () => void;
  onDuplicate?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const TYPE_LABELS: Record<AdditionalFieldType, string> = {
  text: 'Text',
  textarea: 'Text Area',
  dropdown: 'Dropdown',
  number: 'Number',
  date: 'Date',
  'license-picker': 'License Picker',
};

const REQUIRED_LABELS: Record<FieldStrictness, string> = {
  strict: 'Required (blocks save)',
  warning: 'Recommended (warns)',
  optional: 'Optional',
};

export const AdditionalFieldEditor: React.FC<AdditionalFieldEditorProps> = ({
  field,
  siblingFieldKeys,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const setField = <K extends keyof AdditionalField>(key: K, value: AdditionalField[K]) => {
    onChange({ ...field, [key]: value });
  };

  const allowedKeys = siblingFieldKeys.filter((k) => k !== field.fieldKey);
  const resolverOptions = Object.keys(FIELD_DEFAULT_RESOLVER_REGISTRY);

  return (
    <>
      <EditorCard
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        title={
          <>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {field.label || <em style={{ color: '#aaa', fontWeight: 400 }}>(no label)</em>}
            </span>
            <span className="editor-card__id">{field.fieldKey}</span>
            <Chip tone="neutral">{TYPE_LABELS[field.type]}</Chip>
            <Chip tone={field.required === 'strict' ? 'warning' : 'neutral'}>{field.required}</Chip>
          </>
        }
        actions={
          <>
            {onMoveUp && (
              <button type="button" className="editor-icon-btn" title="Move up" onClick={onMoveUp}>
                <IconArrowUp size={15} stroke={1.5} />
              </button>
            )}
            {onMoveDown && (
              <button type="button" className="editor-icon-btn" title="Move down" onClick={onMoveDown}>
                <IconArrowDown size={15} stroke={1.5} />
              </button>
            )}
            {onDuplicate && (
              <button type="button" className="editor-icon-btn" title="Duplicate" onClick={onDuplicate}>
                <IconCopy size={15} stroke={1.5} />
              </button>
            )}
            <button
              type="button"
              className="editor-icon-btn editor-icon-btn--danger"
              title="Delete field"
              onClick={() => setDeleteConfirm(true)}
            >
              <IconTrash size={15} stroke={1.5} />
            </button>
          </>
        }
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Label"
            value={field.label}
            onChange={(e) => setField('label', e.target.value)}
            sx={[{ flex: 1 }, FIELD_SX]}
            required
          />
          <TextField
            size="small"
            label="Field Key"
            value={field.fieldKey}
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace' } } }}
            helperText="Set at creation"
            sx={[{ width: 240 }, FIELD_SX]}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={[{ minWidth: 180 }, FIELD_SX]}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={field.type}
              onChange={(e) => setField('type', e.target.value as AdditionalFieldType)}
            >
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={[{ minWidth: 220 }, FIELD_SX]}>
            <InputLabel>Required</InputLabel>
            <Select
              label="Required"
              value={field.required}
              onChange={(e) => setField('required', e.target.value as FieldStrictness)}
            >
              {Object.entries(REQUIRED_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Placeholder"
            value={field.placeholder ?? ''}
            onChange={(e) => setField('placeholder', e.target.value || undefined)}
            sx={[{ flex: 1 }, FIELD_SX]}
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Default value"
            value={field.defaultValue ?? ''}
            onChange={(e) => setField('defaultValue', e.target.value || undefined)}
            sx={[{ flex: 1 }, FIELD_SX]}
          />
          <TextField
            size="small"
            label="Default from context key"
            value={field.defaultFromContext ?? ''}
            onChange={(e) => setField('defaultFromContext', e.target.value || undefined)}
            sx={[{ flex: 1 }, FIELD_SX]}
            helperText="e.g. firstName, city"
          />
          <Autocomplete
            size="small"
            freeSolo
            options={resolverOptions}
            value={field.defaultResolver ?? ''}
            onChange={(_, v) => setField('defaultResolver', v || undefined)}
            onInputChange={(_, v) => setField('defaultResolver', v || undefined)}
            sx={[{ flex: 1 }, FIELD_SX]}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Default resolver"
                helperText="Function name from registry"
              />
            )}
          />
        </Stack>

        {field.type === 'dropdown' && (
          <>
            <Divider />
            <OptionsEditor
              options={field.options ?? []}
              onChange={(options) => setField('options', options)}
            />
          </>
        )}

        {field.type === 'number' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              label="Derive count from field"
              value={field.deriveCountFrom ?? ''}
              onChange={(e) => setField('deriveCountFrom', e.target.value || undefined)}
              sx={[{ flex: 1 }, FIELD_SX]}
              helperText="Auto-fill with the count of non-empty lines of the named field (e.g. a Text Area), until manually overridden"
            />
          </Stack>
        )}

        {field.type === 'date' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              size="small"
              label="Must be on or after"
              value={field.minDateField ?? ''}
              onChange={(e) => setField('minDateField', e.target.value || undefined)}
              sx={[{ flex: 1 }, FIELD_SX]}
              helperText="Another date field this one can't be earlier than (e.g. End Date ≥ Start Date). Blocks Save on the ticket form."
            >
              <MenuItem value="">
                <em>No rule</em>
              </MenuItem>
              {allowedKeys.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        {field.type === 'license-picker' && (
          <>
            <Divider />
            <LicensePickerConfigEditor
              value={field.licensePicker}
              onChange={(next: LicensePickerConfig) => setField('licensePicker', next)}
            />
          </>
        )}

        <Divider />
        <ConditionEditor
          value={field.showWhen}
          onChange={(next) =>
            setField('showWhen', next === undefined ? undefined : (next ?? undefined))
          }
          allowedFieldKeys={allowedKeys}
          allowCustom
          label="Show When"
        />
      </EditorCard>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Field</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete field "{field.label || field.fieldKey}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)} sx={CANCEL_BTN_SX}>Cancel</Button>
          <Button
            onClick={() => {
              setDeleteConfirm(false);
              onDelete();
            }}
            color="error"
            variant="contained"
            disableElevation
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
